import { AccountGroup } from '@amzn/aac-chrome-internal-api';
import * as React from 'react';
import { AccountTypeFilter } from './IA2Types';

type UseAccountDropdownSearchProps = {
  accounts: AccountGroup[];
};

export type UseAccountDropdownSearchResponse = {
  onSearch: any;
  onFilter: (filters: AccountTypeFilter[]) => void;
  accountTypeList: AccountGroup[];
  accountTypeFilterList: AccountTypeFilter[];
  onClear: () => void;
  searchValue: string;
};

export const useAccountDropdownSearch = ({
  accounts,
}: UseAccountDropdownSearchProps): UseAccountDropdownSearchResponse => {
  const [accountTypeList, setAccountTypeList] = React.useState<AccountGroup[]>(
    accounts || [],
  );
  const selectedFilters = React.useRef<string[]>([]);
  const searchWord = React.useRef<string>('');
  const [accountTypeFilterList, setAccountTypeFilterList] = React.useState(
    accounts.map((account) => {
      return {
        label: account.label,
        type: account.accountType,
        isSelected: true, // default everything to selected
      };
    }),
  );

  const filterAccounts = () => {
    if (selectedFilters.current.length > 0) {
      return accounts.filter(
        (account) => !selectedFilters.current.includes(account.accountType),
      );
    }
    return [...accounts];
  };

  const searchAccounts = (newAccountTypeList: AccountGroup[]) => {
    const regex = new RegExp(searchWord.current, 'i'); // ignore case
    return newAccountTypeList.map((accountType) => {
      return {
        ...accountType,
        accounts: accountType.accounts.filter((account) => {
          // combine list of values for search
          const combinedString = [
            account.label,
            account.entityId,
            account.marketplaceName,
          ].join('|');
          return regex.test(combinedString);
        }),
      };
    });
  };

  const setNewAccountTypeFilterList = (oldAccountTypeFilterList) => {
    return oldAccountTypeFilterList.map((accountTypeFilter) => {
      accountTypeFilter.isSelected = !selectedFilters.current.includes(
        accountTypeFilter.type,
      );
      return accountTypeFilter;
    });
  };

  const handleSearchAndFilter = () => {
    let newAccountTypeList = filterAccounts();

    if (searchWord.current) {
      newAccountTypeList = searchAccounts(newAccountTypeList);
    }

    newAccountTypeList = newAccountTypeList.filter(
      (accountTypeList) => accountTypeList.accounts.length > 0,
    );

    setAccountTypeList(newAccountTypeList);
    setAccountTypeFilterList(setNewAccountTypeFilterList);
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    searchWord.current = e.target.value;
    handleSearchAndFilter();
  };

  const onFilter = (accountTypes: AccountTypeFilter[]) => {
    selectedFilters.current = accountTypes
      .filter((accountType) => !accountType.isSelected)
      .map((filter) => filter.type);
    handleSearchAndFilter();
  };

  const onClear = () => {
    searchWord.current = '';
    handleSearchAndFilter();
  };

  return {
    onSearch,
    accountTypeList,
    onFilter,
    accountTypeFilterList,
    onClear,
    searchValue: searchWord.current,
  };
};
